/**
 * @quotech/ui - Locked Tab Content
 * 
 * PATH: packages/ui/src/components/settings/LockedTabContent.tsx
 * PURPOSE: Display a locked state for admin-only settings tabs when viewed by non-admin users
 * 
 * WHY: Non-admin users should see that settings exist (not hidden) but understand
 *      they don't have permission to access them, with clear UX and accessibility.
 *
 * FLOW:
 *   ┌──────────────┐    ┌───────────────┐    ┌──────────────┐
 *   │ Tab selected │───▶│ Check locked  │───▶│ Render locked│
 *   └──────────────┘    └───────────────┘    │ content      │
 *                                            └──────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: Lock, ShieldAlert icons
 *   - cn(): class composition helper
 */

import { Lock, ShieldAlert, Users, Mail } from 'lucide-react'
import { cn } from '../../lib/utils'

// =============================================================================
// TYPES
// =============================================================================

export interface LockedTabContentProps {
  /** The tab ID that is locked */
  tabId: string
  /** The tab label for display */
  tabLabel: string
  /** Optional custom message (overrides default) */
  customMessage?: string
  /** Optional list of admin emails/names to contact */
  admins?: Array<{
    name: string
    email?: string
  }>
  /** Callback when "Request Access" is clicked */
  onRequestAccess?: () => void
  /** Custom className */
  className?: string
}

// =============================================================================
// LOCKED TAB COPY VARIANTS
// =============================================================================

/**
 * Get contextual copy for different locked tabs
 */
function getLockedTabCopy(tabId: string, tabLabel: string): { title: string; description: string } {
  const copyMap: Record<string, { title: string; description: string }> = {
    organisation: {
      title: 'Organisation Settings',
      description: 'Organisation-level settings like company name, address, and business details are managed by administrators.',
    },
    entities: {
      title: 'Entity Management',
      description: 'Legal entities and Xero tenant mapping require administrator access to configure.',
    },
    branding: {
      title: 'Branding Settings',
      description: 'Logo, colors, and visual branding are controlled at the organisation level by administrators.',
    },
    integrations: {
      title: 'Integration Settings',
      description: 'Connecting and managing third-party integrations requires administrator privileges.',
    },
    team: {
      title: 'Team Management',
      description: 'Adding, removing, and managing team member roles requires administrator access.',
    },
    'access-control': {
      title: 'Access Control',
      description: 'Page-level permissions and role configuration are restricted to administrators.',
    },
    'module-access': {
      title: 'Module Access',
      description: 'Enabling or disabling hub modules for your organisation requires administrator privileges.',
    },
    billing: {
      title: 'Billing Settings',
      description: 'Subscription management and billing information are restricted to organisation administrators.',
    },
  }

  return copyMap[tabId] || {
    title: `${tabLabel} Settings`,
    description: `This section requires administrator privileges to access and modify.`,
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

export function LockedTabContent({
  tabId,
  tabLabel,
  customMessage,
  admins,
  onRequestAccess,
  className,
}: LockedTabContentProps) {
  const { title, description } = getLockedTabCopy(tabId, tabLabel)
  const displayDescription = customMessage || description

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-[400px] max-w-md mx-auto text-center px-6',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      {/* Lock Icon */}
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center shadow-inner">
          <Lock className="w-10 h-10 text-slate-400 dark:text-slate-500" />
        </div>
        <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center border-2 border-white dark:border-slate-950">
          <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        </div>
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
        {displayDescription}
      </p>

      {/* Admin Contact List */}
      {admins && admins.length > 0 && (
        <div className="w-full mb-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 mb-3">
            <Users className="w-3.5 h-3.5" />
            <span>Contact an administrator</span>
          </div>
          <div className="space-y-2">
            {admins.slice(0, 3).map((admin, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-slate-700 dark:text-slate-300 font-medium">
                  {admin.name}
                </span>
                {admin.email && (
                  <a
                    href={`mailto:${admin.email}?subject=Access Request: ${title}`}
                    className="text-teal-600 dark:text-teal-400 hover:underline text-xs"
                  >
                    {admin.email}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request Access Button */}
      {onRequestAccess && (
        <button
          onClick={onRequestAccess}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium',
            'bg-teal-600 text-white hover:bg-teal-700',
            'transition-colors shadow-sm',
            'focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950'
          )}
        >
          <Mail className="w-4 h-4" />
          Request Access
        </button>
      )}

      {/* Fallback CTA when no request handler */}
      {!onRequestAccess && !admins?.length && (
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Please contact your organisation administrator for access.
        </p>
      )}

      {/* Screen reader announcement */}
      <span className="sr-only">
        This settings section is locked. {displayDescription}
      </span>
    </div>
  )
}

export default LockedTabContent
